import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { Dices, Home, Trophy, User, Wallet } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n/I18nContext";
import { LangToggle } from "@/components/LangToggle";
import { BalanceChip } from "@/components/BalanceChip";
import { InstallBanner } from "@/components/InstallApp";
import { Logo } from "@/components/Brand";
import { InboxBell } from "@/components/InboxBell";

const NAV = [
  { to: "/", icon: Home, key: "home", end: true },
  { to: "/lobby", icon: Dices, key: "lobby" },
  { to: "/wallet", icon: Wallet, key: "wallet" },
  { to: "/leaderboard", icon: Trophy, key: "board" },
  { to: "/profile", icon: User, key: "profile" },
];

const navCls = ({ isActive }) =>
  `transition-colors duration-200 ${isActive ? "text-amber-400" : "text-slate-400 hover:text-slate-100"}`;

const Header = () => {
  const { user } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  return (
    <header className="glass sticky top-0 z-40 border-b border-white/10">
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-3 px-4 sm:px-6">
        <Logo />
        <nav className="hidden items-center gap-7 text-sm font-medium md:flex">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} className={navCls} data-testid={`desktop-nav-${n.key}`}>
              {t(`nav.${n.key}`)}
            </NavLink>
          ))}
        </nav>
        <div className="flex shrink-0 items-center gap-2">
          <LangToggle />
          {user ? (
            <>
              <InboxBell />
              <BalanceChip />
            </>
          ) : (
            <button
              onClick={() => navigate("/auth")}
              className="h-10 rounded-full bg-amber-500 px-4 text-sm font-semibold text-[#0B0D12] transition-transform duration-150 active:scale-95"
              data-testid="header-login-btn"
            >
              {t("nav.login")}
            </button>
          )}
        </div>
      </div>
    </header>
  );
};

const BottomNav = () => {
  const { t } = useI18n();
  return (
    <nav className="glass safe-bottom fixed bottom-0 left-0 right-0 z-50 border-t border-white/10 md:hidden" data-testid="bottom-nav">
      <div className="flex items-center justify-around px-2 py-1.5">
        {NAV.map(({ to, icon: Icon, key, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            data-testid={`bottom-nav-${key}`}
            className={({ isActive }) =>
              `flex min-h-[52px] min-w-[56px] flex-col items-center justify-center gap-1 rounded-xl text-[11px] font-medium transition-colors ${
                isActive ? "text-amber-400" : "text-slate-400"
              }`
            }
          >
            <Icon className="h-5 w-5" />
            {t(`nav.${key}`)}
          </NavLink>
        ))}
      </div>
    </nav>
  );
};

export const Layout = () => (
  <div className="grain relative min-h-screen bg-[#0B0D12]">
    <Header />
    <main className="relative z-10 mx-auto max-w-6xl px-4 pb-28 pt-6 sm:px-6 md:pb-16">
      <InstallBanner />
      <Outlet />
    </main>
    <BottomNav />
  </div>
);
